/**
 * Template Specification for "stackedBarChart" Chart
 *
 * This template defines the structure and default configuration for a "stacked_bar" chart.
 *
 * Key Categories:
 * 1. Standard Fields:
 *    - 'type': Specifies the chart type (here, "stacked_bar").
 *    - 'title': A dynamic text field expecting a string input (denoted by "$string").
 *
 * 2. Label Fields:
 *    - Keys with a single underscore (e.g., '_type', '_title') provide user-friendly labels
 *      for the corresponding fields.
 *
 * 3. Default Fields:
 *    - Keys with double underscores (e.g., '__title') specify default values. Here,
 *      '__title': '%Stacked Bar Chart' means the default title is "Stacked Bar Chart"
 *      (the "%" is removed to obtain the default value).
 *
 * 4. Dynamic Fields:
 *    - The key 'bar#_metric' uses the "#" placeholder to indicate that it can represent
 *      multiple dynamic fields (e.g., "bar1_metric", "bar2_metric", etc.).
 *    - Its value "$dynamic" implies that these fields are expected to be managed dynamically,
 *      typically allowing for multiple entries.
 *    - The corresponding label '_bar#_metric' ("Bar # Metric") will update with the dynamic
 *      index to label each field appropriately.
 *
 * This specification provides a flexible framework for initializing stacked bar charts,
 * where key fields can be dynamically replicated to accommodate multiple bar metrics,
 * and default values and user-friendly labels are defined for ease of use.
 */
export const stackedBarChart = {
    'type': 'stacked_bar_chart',
    '_type': 'Stacked Bar Chart',
    'title': '$string',
    '_title': 'Title',
    '__title': '%Stacked Bar Chart',
    'bar#_metric': '$dynamic',
    '_bar#_metric': "Bar # Metric",
}

// Example chart configuration for "stacked_bar" chart. Used in preview mode
const getColorsBySeriesLength = (seriesMetrics,index) => {
    let colors = ["#9B9C9E","#009FD7","#009B4D","#EDA500","#D10E33","#D10E33","#D10E33","#D10E33"];
    if (seriesMetrics.length === 4) {
        colors = ['#31B452', '#FFC83D', '#EF7616', '#d9534f'];
    } else if (seriesMetrics.length === 3) {
        colors = ['#31B452', '#FFC83D', '#d9534f'];
    } else if (seriesMetrics.length === 2) {
        colors = ['#31B452', '#d9534f'];
    }
    return colors[index];
};

const makeDummyData  = (labels) => {
    return labels.map((label,index )=> {
        return({
            "name": label,
            "data": [
                {
                    "y": 100+(index * 8)*(10-index),
                    "totalDistance": 500+40,
                    "isRestOrTravel": false,
                    "drilldown": "03-04"
                },
                {
                    "y": 140+(index * 10)*(10-index),
                    "totalDistance": 500+20,
                    "isRestOrTravel": false,
                    "drilldown": "03-06"
                },
                {
                    "y": 120+(index * 9)*(10-index),
                    "totalDistance": 500+30,
                    "isRestOrTravel": false,
                    "drilldown": "03-08"
                }
            ],
            "color": getColorsBySeriesLength(labels,index),
            "visible": true
        })
    })
}
export const stackedBarExample = (title, stackedBarLabels) => ({
    "chart": {
        "type": "column",
        "zoomType": "xy",
        "events": {},
        "height": "100%"
    },
    "series": makeDummyData(stackedBarLabels),
    "drilldown": {
        "breadcrumbs": {
            "floating": true
        },
        "activeAxisLabelStyle": {
            "cursor": "default",
            "color": "rgb(51, 51, 51)",
            "textDecoration": "none",
            "fontWeight": 500,
            "tickmarkPlacement": "between",
            "tickWidth": 1
        },
        "activeDataLabelStyle": {
            "cursor": "default",
            "color": "rgb(51, 51, 51)",
            "textDecoration": "none"
        },
        "series": []
    },
    "title": {
        "text": title
    },
    "xAxis": {
        "categories": [
            "03-04",
            "03-06",
            "03-08"
        ],
        "gridLineWidth": 2,
        "labels": {
            "rotation": 0
        },
        "tickmarkPlacement": "between",
        "tickWidth": 1
    },
    "yAxis": [
        {
            "title": {
                "text": null
            },
            "stackLabels": {
                "enabled": true
            },
            "max": stackedBarLabels.length * 500,
            "startOnTick": true,
            "endOnTick": true
        },
        {
            "title": {
                "text": null
            },
            "opposite": true,
            "min": 0,
            "max": stackedBarLabels.length * 500,
            "visible": false,
            "startOnTick": true,
            "endOnTick": true,
            "tickInterval": 0.504
        }
    ],
    "legend": {
        "enabled": true,
        "verticalAlign": "top",
        "layout": "horizontal"
    },
    "tooltip": {
        "shared": true
    },
    "plotOptions": {
        "column": {
            "borderWidth": 0,
            "groupPadding": 0.05,
            "pointPadding": 0.05,
            "stacking": "normal",
            "dataLabels": {
                "enabled": false,
                "style": {
                    "fontWeight": "bold",
                    "color": "white",
                    "textOutline": "none",
                    "fontSize": "11px"
                }
            }
        }
    },
    "exporting": {
        "enabled": false
    }
})